import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { parseServerError } from "utils/errors";
import {
  REGISTRATION_TRACKER_ALLOWED_STATUS_VALUES,
  resolveRegistrationTrackerStatusValue,
} from "constants/registrationTrackerStatuses";
import {
  deleteRegistrationTracker,
  getRegistrationTracker,
  getRegistrationTrackerHistory,
  getRegistrationTrackers,
  getRegistrationTrackerStates,
  getRegistrationTrackerTypes,
  getRegistrationTrackerReferences,
  putRegistrationTracker,
  postRegistrationTracker,
  postRegistrationTrackerStatus,
  postRegistrationTrackerComment,
  putRegistrationTrackerComment,
  deleteRegistrationTrackerComment as deleteRegistrationTrackerCommentRequest,
} from "./registrationTrackerApi";

function normalizeTrackerList(payload) {
  const raw = Array.isArray(payload)
    ? payload
    : payload?.items && Array.isArray(payload.items)
      ? payload.items
      : payload?.data && Array.isArray(payload.data)
        ? payload.data
        : payload?.results && Array.isArray(payload.results)
          ? payload.results
          : [];
  return raw.map((row) => {
    if (!row || typeof row !== "object") return row;
    return {
      ...row,
      comments: Array.isArray(row.comments) ? row.comments : [],
    };
  });
}

function normalizeStates(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload?.items && Array.isArray(payload.items)) return payload.items;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  if (payload?.states && Array.isArray(payload.states)) return payload.states;
  return [];
}

function normalizeRegistrationTrackerTypes(payload) {
  const raw = Array.isArray(payload)
    ? payload
    : payload?.items && Array.isArray(payload.items)
      ? payload.items
      : payload?.data && Array.isArray(payload.data)
        ? payload.data
        : payload?.types && Array.isArray(payload.types)
          ? payload.types
          : [];
  return raw
    .filter((entry) => entry != null && typeof entry === "object")
    .map((entry) => {
      const code = String(entry.code ?? "").trim();
      const name = String(entry.name ?? entry.label ?? code).trim();
      const categoriesRaw = Array.isArray(entry.categories) ? entry.categories : [];
      const categories = categoriesRaw
        .filter((c) => c != null && typeof c === "object")
        .map((c) => ({
          code: String(c.code ?? "").trim(),
          name: String(c.name ?? c.label ?? c.code ?? "").trim(),
        }))
        .filter((c) => c.code !== "");
      const operationsRaw = Array.isArray(entry.operation) ? entry.operation : [];
      const operation = operationsRaw
        .filter((op) => op != null && typeof op === "object")
        .map((op) => ({
          code: String(op.code ?? "").trim(),
          name: String(op.name ?? op.label ?? op.code ?? "").trim(),
        }))
        .filter((op) => op.code !== "");
      return { code, name: name || code, categories, operation };
    })
    .filter((entry) => entry.code !== "");
}

function normalizeRegistrationTrackerHistory(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload?.items && Array.isArray(payload.items)) return payload.items;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  if (payload?.history && Array.isArray(payload.history)) return payload.history;
  return [];
}

function unwrapReferencesPayload(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return payload;
  }
  if (
    Array.isArray(payload.registrators) ||
    Array.isArray(payload.companies) ||
    Array.isArray(payload.countries) ||
    Array.isArray(payload.types)
  ) {
    return payload;
  }
  if (
    payload.data &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data)
  ) {
    return payload.data;
  }
  return payload;
}

function pickObjectList(payload, key) {
  const source = unwrapReferencesPayload(payload);
  if (source?.[key] && Array.isArray(source[key])) return source[key];
  if (Array.isArray(source) && key === "registrators") return source;
  return [];
}

function normalizeIdNameList(raw) {
  return (Array.isArray(raw) ? raw : [])
    .filter((entry) => entry != null && typeof entry === "object")
    .map((entry) => {
      const idRaw = entry.id;
      if (idRaw === undefined || idRaw === null) return null;
      const id = String(idRaw).trim();
      if (id === "") return null;
      const name = String(entry.name ?? entry.label ?? id).trim();
      return { id, name: name || id };
    })
    .filter(Boolean);
}

function normalizeCodeNameList(raw) {
  return (Array.isArray(raw) ? raw : [])
    .filter((entry) => entry != null && typeof entry === "object")
    .map((entry) => {
      const code = String(entry.code ?? "").trim();
      if (code === "") return null;
      const name = String(entry.name ?? entry.label ?? code).trim();
      return { code, name: name || code };
    })
    .filter(Boolean);
}

function normalizeRegistrationTrackerRegistrators(payload) {
  const raw = Array.isArray(payload)
    ? payload
    : pickObjectList(payload, "registrators");
  return (Array.isArray(raw) ? raw : [])
    .filter((entry) => entry != null && typeof entry === "object")
    .map((entry) => {
      const idRaw = entry.id;
      if (idRaw === undefined || idRaw === null) return null;
      const id = String(idRaw).trim();
      if (id === "") return null;
      const name = String(entry.name ?? entry.label ?? id).trim();
      return {
        id,
        name: name || id,
        email: String(entry.email ?? "").trim(),
        role: String(entry.role ?? "").trim(),
        countries: Array.isArray(entry.countries) ? entry.countries : [],
      };
    })
    .filter(Boolean);
}

function normalizeRegistrationTrackerReferences(payload) {
  const source = unwrapReferencesPayload(payload);
  return {
    registrators: normalizeRegistrationTrackerRegistrators(source),
    companies: normalizeIdNameList(pickObjectList(source, "companies")),
    countries: normalizeCodeNameList(pickObjectList(source, "countries")),
    types: normalizeCodeNameList(pickObjectList(source, "types")),
  };
}

function historyTrackerKey(trackerId) {
  return String(trackerId ?? "").trim();
}

function rowMatchesTrackerId(row, trackerId) {
  const id = String(trackerId ?? "").trim();
  if (!id || !row || typeof row !== "object") return false;
  const rowId = row.id ?? row.tracker_id ?? row.trackerId;
  return rowId != null && String(rowId) === id;
}

function patchRowHighlighted(row, highlighted) {
  const base = row && typeof row === "object" ? row : {};
  return { ...base, highlighted: highlighted === true };
}

function appendCommentOnRow(row, comment) {
  const base = row && typeof row === "object" ? row : {};
  const existing = Array.isArray(base.comments) ? base.comments : [];
  if (!comment || typeof comment !== "object") {
    return { ...base, comments: existing };
  }
  const commentId = comment.id;
  if (commentId != null && existing.some((item) => String(item?.id) === String(commentId))) {
    return {
      ...base,
      comments: existing.map((item) =>
        String(item?.id) === String(commentId) ? { ...item, ...comment } : item
      ),
    };
  }
  return { ...base, comments: [...existing, comment] };
}

function mergeCommentOnRow(row, comment) {
  const base = row && typeof row === "object" ? row : {};
  const existing = Array.isArray(base.comments) ? base.comments : [];
  if (!comment || typeof comment !== "object") {
    return { ...base, comments: existing };
  }
  const commentId = comment.id;
  if (commentId == null || String(commentId).trim() === "") {
    return { ...base, comments: existing };
  }
  return {
    ...base,
    comments: existing.map((item) =>
      String(item?.id) === String(commentId) ? { ...item, ...comment } : item
    ),
  };
}

function removeCommentOnRow(row, commentId) {
  const base = row && typeof row === "object" ? row : {};
  const existing = Array.isArray(base.comments) ? base.comments : [];
  const id = String(commentId ?? "").trim();
  if (!id) return { ...base, comments: existing };
  return {
    ...base,
    comments: existing.filter((item) => String(item?.id) !== id),
  };
}

function parseCommentFromResponse(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const comment =
    data.comment && typeof data.comment === "object" && !Array.isArray(data.comment)
      ? data.comment
      : data;
  if (!comment || typeof comment !== "object" || Array.isArray(comment)) return null;
  return comment;
}

function normalizeCommentVisibility(value) {
  return String(value ?? "").trim().toLowerCase() === "external"
    ? "external"
    : "internal";
}

export const fetchRegistrationTrackers = createAsyncThunk(
  "registrationTracker/fetchList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRegistrationTrackers();
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return normalizeTrackerList(data);
    } catch (error) {
      return rejectWithValue(parseServerError(error) || "Error");
    }
  }
);

export const fetchRegistrationTrackerHistory = createAsyncThunk(
  "registrationTracker/fetchHistory",
  async (trackerId, { rejectWithValue }) => {
    const key = historyTrackerKey(trackerId);
    if (!key) {
      return rejectWithValue("Tracker id is required");
    }
    try {
      const response = await getRegistrationTrackerHistory(key);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return {
        trackerId: key,
        items: normalizeRegistrationTrackerHistory(data),
      };
    } catch (error) {
      return rejectWithValue(parseServerError(error) || "Error");
    }
  }
);

export const fetchRegistrationTrackerDetail = createAsyncThunk(
  "registrationTracker/fetchDetail",
  async (trackerId, { rejectWithValue }) => {
    try {
      const response = await getRegistrationTracker(trackerId);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      if (!data || typeof data !== "object" || Array.isArray(data)) return data;
      return {
        ...data,
        comments: Array.isArray(data.comments) ? data.comments : [],
      };
    } catch (error) {
      return rejectWithValue(parseServerError(error) || "Error");
    }
  }
);

export const createRegistrationTracker = createAsyncThunk(
  "registrationTracker/create",
  async (body, { rejectWithValue }) => {
    try {
      const response = await postRegistrationTracker(body);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return Array.isArray(data) ? data : data != null ? [data] : [];
    } catch (error) {
      return rejectWithValue(parseServerError(error) || "Error");
    }
  }
);

export const updateRegistrationTracker = createAsyncThunk(
  "registrationTracker/update",
  async ({ trackerId, body }, { rejectWithValue }) => {
    try {
      const response = await putRegistrationTracker(trackerId, body);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(parseServerError(error) || "Error");
    }
  }
);

export const updateRegistrationTrackerStatus = createAsyncThunk(
  "registrationTracker/updateStatus",
  async ({ trackerId, status }, { rejectWithValue }) => {
    const id = String(trackerId ?? "").trim();
    const nextStatus = resolveRegistrationTrackerStatusValue(status);
    if (!id) {
      return rejectWithValue("Tracker id is required");
    }
    if (!REGISTRATION_TRACKER_ALLOWED_STATUS_VALUES.has(nextStatus)) {
      return rejectWithValue("Select a valid status");
    }
    try {
      const response = await postRegistrationTrackerStatus(id, nextStatus);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return { trackerId: id, status: nextStatus };
    } catch (error) {
      return rejectWithValue(parseServerError(error) || "Error");
    }
  }
);

export const updateRegistrationTrackerHighlighted = createAsyncThunk(
  "registrationTracker/updateHighlighted",
  async ({ trackerId, highlighted }, { rejectWithValue }) => {
    const id = String(trackerId ?? "").trim();
    if (!id) {
      return rejectWithValue("Tracker id is required");
    }
    const nextHighlighted = highlighted === true;
    try {
      const response = await putRegistrationTracker(id, {
        highlighted: nextHighlighted,
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return { trackerId: id, highlighted: nextHighlighted };
    } catch (error) {
      return rejectWithValue(parseServerError(error) || "Error");
    }
  }
);

export const removeRegistrationTracker = createAsyncThunk(
  "registrationTracker/delete",
  async (trackerId, { rejectWithValue }) => {
    try {
      const response = await deleteRegistrationTracker(trackerId);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return trackerId;
    } catch (error) {
      return rejectWithValue(parseServerError(error) || "Error");
    }
  }
);

export const createRegistrationTrackerComment = createAsyncThunk(
  "registrationTracker/createComment",
  async ({ trackerId, body }, { rejectWithValue }) => {
    const id = String(trackerId ?? "").trim();
    const text = String(body?.text ?? "").trim();
    const visibility = normalizeCommentVisibility(body?.visibility ?? "internal");
    if (!id) {
      return rejectWithValue("Tracker id is required");
    }
    if (!text) {
      return rejectWithValue("Comment text is required");
    }
    try {
      const response = await postRegistrationTrackerComment(id, {
        visibility,
        text,
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      const comment = parseCommentFromResponse(data);
      if (!comment) {
        return rejectWithValue("Error");
      }
      return { trackerId: id, comment };
    } catch (error) {
      return rejectWithValue(parseServerError(error) || "Error");
    }
  }
);

export const updateRegistrationTrackerComment = createAsyncThunk(
  "registrationTracker/updateComment",
  async ({ trackerId, commentId, body }, { rejectWithValue }) => {
    const id = String(trackerId ?? "").trim();
    const cid = String(commentId ?? "").trim();
    if (!id) {
      return rejectWithValue("Tracker id is required");
    }
    if (!cid) {
      return rejectWithValue("Comment id is required");
    }
    const patch = {};
    if (body && Object.prototype.hasOwnProperty.call(body, "text")) {
      const text = String(body.text ?? "").trim();
      if (!text) {
        return rejectWithValue("Comment text is required");
      }
      patch.text = text;
    }
    if (body && Object.prototype.hasOwnProperty.call(body, "visibility")) {
      patch.visibility = normalizeCommentVisibility(body.visibility);
    }
    if (Object.keys(patch).length === 0) {
      return rejectWithValue("No changes");
    }
    try {
      const response = await putRegistrationTrackerComment(id, cid, patch);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      const comment = parseCommentFromResponse(data);
      if (!comment) {
        return rejectWithValue("Error");
      }
      return {
        trackerId: id,
        comment: comment.id != null ? comment : { ...comment, id: cid },
      };
    } catch (error) {
      return rejectWithValue(parseServerError(error) || "Error");
    }
  }
);

export const deleteRegistrationTrackerComment = createAsyncThunk(
  "registrationTracker/deleteComment",
  async ({ trackerId, commentId }, { rejectWithValue }) => {
    const id = String(trackerId ?? "").trim();
    const cid = String(commentId ?? "").trim();
    if (!id) {
      return rejectWithValue("Tracker id is required");
    }
    if (!cid) {
      return rejectWithValue("Comment id is required");
    }
    try {
      const response = await deleteRegistrationTrackerCommentRequest(id, cid);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return { trackerId: id, commentId: cid };
    } catch (error) {
      return rejectWithValue(parseServerError(error) || "Error");
    }
  }
);

export const fetchRegistrationTrackerTypes = createAsyncThunk(
  "registrationTracker/fetchTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRegistrationTrackerTypes();
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return normalizeRegistrationTrackerTypes(data);
    } catch (error) {
      return rejectWithValue(parseServerError(error) || "Error");
    }
  }
);

export const fetchRegistrationTrackerReferences = createAsyncThunk(
  "registrationTracker/fetchReferences",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRegistrationTrackerReferences();
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return normalizeRegistrationTrackerReferences(data);
    } catch (error) {
      return rejectWithValue(parseServerError(error) || "Error");
    }
  }
);

export const fetchRegistrationTrackerRegistrators =
  fetchRegistrationTrackerReferences;

export const fetchRegistrationTrackerStates = createAsyncThunk(
  "registrationTracker/fetchStates",
  async (countryId, { rejectWithValue }) => {
    const normalized = String(countryId ?? "")
      .trim()
      .toUpperCase();
    if (!normalized) {
      return rejectWithValue("Country is required");
    }
    try {
      const response = await getRegistrationTrackerStates(normalized);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return { countryId: normalized, states: normalizeStates(data) };
    } catch (error) {
      return rejectWithValue(parseServerError(error) || "Error");
    }
  }
);

const initialState = {
  list: [],
  listLoading: false,
  listError: null,
  detail: null,
  detailLoading: false,
  saving: false,
  saveError: null,
  commentSaving: false,
  commentError: null,
  statusSaving: false,
  statusError: null,
  commentDeleting: false,
  commentDeleteError: null,
  deletingId: null,
  deleteError: null,
  highlightingById: {},
  statesByCountry: {},
  statesLoadingCountry: null,
  statesError: null,
  types: [],
  typesLoading: false,
  typesError: null,
  registrators: [],
  registratorsLoading: false,
  registratorsError: null,
  referenceCompanies: [],
  referenceCountries: [],
  referenceTypes: [],
  referencesLoaded: false,
  historyByTrackerId: {},
  historyLoadingByTrackerId: {},
  historyErrorByTrackerId: {},
};

const registrationTrackerSlice = createSlice({
  name: "registrationTracker",
  initialState,
  reducers: {
    clearRegistrationTrackerDetail: (state) => {
      state.detail = null;
      state.detailLoading = false;
    },
    clearRegistrationTrackerErrors: (state) => {
      state.listError = null;
      state.saveError = null;
      state.commentError = null;
      state.commentDeleteError = null;
      state.statusError = null;
      state.deleteError = null;
      state.statesError = null;
      state.typesError = null;
      state.registratorsError = null;
    },
    clearRegistrationTrackerTypesError: (state) => {
      state.typesError = null;
    },
    clearRegistrationTrackerRegistratorsError: (state) => {
      state.registratorsError = null;
    },
    clearRegistrationTrackerStatesError: (state) => {
      state.statesError = null;
    },
    clearRegistrationTrackerStatesCache: (state) => {
      state.statesByCountry = {};
      state.statesLoadingCountry = null;
      state.statesError = null;
    },
    clearRegistrationTrackerHistoryError: (state, action) => {
      const key = historyTrackerKey(action.payload);
      if (!key) return;
      delete state.historyErrorByTrackerId[key];
    },
    clearRegistrationTrackerCommentError: (state) => {
      state.commentError = null;
    },
    clearRegistrationTrackerStatusError: (state) => {
      state.statusError = null;
    },
    clearRegistrationTrackerCommentDeleteError: (state) => {
      state.commentDeleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRegistrationTrackers.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchRegistrationTrackers.fulfilled, (state, action) => {
        state.listLoading = false;
        state.list = action.payload;
      })
      .addCase(fetchRegistrationTrackers.rejected, (state, action) => {
        state.listLoading = false;
        state.list = [];
        state.listError = parseServerError(action.payload ?? action.error);
      });

    builder
      .addCase(fetchRegistrationTrackerDetail.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(fetchRegistrationTrackerDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action.payload;
      })
      .addCase(fetchRegistrationTrackerDetail.rejected, (state) => {
        state.detailLoading = false;
        state.detail = null;
      });

    builder
      .addCase(createRegistrationTracker.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(createRegistrationTracker.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createRegistrationTracker.rejected, (state, action) => {
        state.saving = false;
        state.saveError = parseServerError(action.payload ?? action.error);
      });

    builder
      .addCase(updateRegistrationTracker.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(updateRegistrationTracker.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateRegistrationTracker.rejected, (state, action) => {
        state.saving = false;
        state.saveError = parseServerError(action.payload ?? action.error);
      });

    builder
      .addCase(updateRegistrationTrackerHighlighted.pending, (state, action) => {
        if (!state.highlightingById) state.highlightingById = {};
        const id = String(action.meta.arg?.trackerId ?? "").trim();
        if (id) state.highlightingById[id] = true;
        state.saveError = null;
      })
      .addCase(updateRegistrationTrackerHighlighted.fulfilled, (state, action) => {
        if (!state.highlightingById) state.highlightingById = {};
        const { trackerId, highlighted } = action.payload;
        delete state.highlightingById[trackerId];
        const nextHighlighted = highlighted === true;
        if (Array.isArray(state.list)) {
          state.list = state.list.map((item) =>
            rowMatchesTrackerId(item, trackerId)
              ? patchRowHighlighted(item, nextHighlighted)
              : item
          );
        }
        if (rowMatchesTrackerId(state.detail, trackerId)) {
          state.detail = patchRowHighlighted(state.detail, nextHighlighted);
        }
      })
      .addCase(updateRegistrationTrackerHighlighted.rejected, (state, action) => {
        if (!state.highlightingById) state.highlightingById = {};
        const id = String(action.meta.arg?.trackerId ?? "").trim();
        if (id) delete state.highlightingById[id];
        state.saveError = parseServerError(action.payload ?? action.error);
      });

    builder
      .addCase(removeRegistrationTracker.pending, (state, action) => {
        state.deletingId = action.meta.arg;
        state.deleteError = null;
      })
      .addCase(removeRegistrationTracker.fulfilled, (state, action) => {
        state.deletingId = null;
        const id = action.payload;
        state.list = state.list.filter((row) => String(row.id) !== String(id));
      })
      .addCase(removeRegistrationTracker.rejected, (state, action) => {
        state.deletingId = null;
        state.deleteError = parseServerError(action.payload ?? action.error);
      });

    builder
      .addCase(createRegistrationTrackerComment.pending, (state) => {
        state.commentSaving = true;
        state.commentError = null;
      })
      .addCase(createRegistrationTrackerComment.fulfilled, (state, action) => {
        state.commentSaving = false;
        const { trackerId, comment } = action.payload;
        if (Array.isArray(state.list)) {
          state.list = state.list.map((item) =>
            rowMatchesTrackerId(item, trackerId)
              ? appendCommentOnRow(item, comment)
              : item
          );
        }
        if (rowMatchesTrackerId(state.detail, trackerId)) {
          state.detail = appendCommentOnRow(state.detail, comment);
        }
      })
      .addCase(createRegistrationTrackerComment.rejected, (state, action) => {
        state.commentSaving = false;
        state.commentError = parseServerError(action.payload ?? action.error);
      });

    builder
      .addCase(updateRegistrationTrackerComment.pending, (state) => {
        state.commentSaving = true;
        state.commentError = null;
      })
      .addCase(updateRegistrationTrackerComment.fulfilled, (state, action) => {
        state.commentSaving = false;
        const { trackerId, comment } = action.payload;
        if (Array.isArray(state.list)) {
          state.list = state.list.map((item) =>
            rowMatchesTrackerId(item, trackerId)
              ? mergeCommentOnRow(item, comment)
              : item
          );
        }
        if (rowMatchesTrackerId(state.detail, trackerId)) {
          state.detail = mergeCommentOnRow(state.detail, comment);
        }
      })
      .addCase(updateRegistrationTrackerComment.rejected, (state, action) => {
        state.commentSaving = false;
        state.commentError = parseServerError(action.payload ?? action.error);
      });

    builder
      .addCase(deleteRegistrationTrackerComment.pending, (state) => {
        state.commentDeleting = true;
        state.commentDeleteError = null;
      })
      .addCase(deleteRegistrationTrackerComment.fulfilled, (state, action) => {
        state.commentDeleting = false;
        const { trackerId, commentId } = action.payload;
        if (Array.isArray(state.list)) {
          state.list = state.list.map((item) =>
            rowMatchesTrackerId(item, trackerId)
              ? removeCommentOnRow(item, commentId)
              : item
          );
        }
        if (rowMatchesTrackerId(state.detail, trackerId)) {
          state.detail = removeCommentOnRow(state.detail, commentId);
        }
      })
      .addCase(deleteRegistrationTrackerComment.rejected, (state, action) => {
        state.commentDeleting = false;
        state.commentDeleteError = parseServerError(action.payload ?? action.error);
      });

    builder
      .addCase(fetchRegistrationTrackerTypes.pending, (state) => {
        state.typesLoading = true;
        state.typesError = null;
      })
      .addCase(fetchRegistrationTrackerTypes.fulfilled, (state, action) => {
        state.typesLoading = false;
        state.types = action.payload;
      })
      .addCase(fetchRegistrationTrackerTypes.rejected, (state, action) => {
        state.typesLoading = false;
        state.typesError = parseServerError(action.payload ?? action.error);
      });

    builder
      .addCase(fetchRegistrationTrackerReferences.pending, (state) => {
        state.registratorsLoading = true;
        state.registratorsError = null;
      })
      .addCase(fetchRegistrationTrackerReferences.fulfilled, (state, action) => {
        state.registratorsLoading = false;
        state.referencesLoaded = true;
        const payload = action.payload ?? {};
        state.registrators = Array.isArray(payload)
          ? payload
          : payload.registrators ?? [];
        state.referenceCompanies = payload.companies ?? [];
        state.referenceCountries = payload.countries ?? [];
        state.referenceTypes = payload.types ?? [];
      })
      .addCase(fetchRegistrationTrackerReferences.rejected, (state, action) => {
        state.registratorsLoading = false;
        state.registratorsError = parseServerError(action.payload ?? action.error);
      });

    builder
      .addCase(fetchRegistrationTrackerStates.pending, (state, action) => {
        const key = String(action.meta.arg ?? "")
          .trim()
          .toUpperCase();
        state.statesLoadingCountry = key || action.meta.arg;
        state.statesError = null;
      })
      .addCase(fetchRegistrationTrackerStates.fulfilled, (state, action) => {
        state.statesLoadingCountry = null;
        const { countryId, states } = action.payload;
        state.statesByCountry[countryId] = states;
      })
      .addCase(fetchRegistrationTrackerStates.rejected, (state, action) => {
        state.statesLoadingCountry = null;
        state.statesError = parseServerError(action.payload ?? action.error);
        const key = String(action.meta.arg ?? "")
          .trim()
          .toUpperCase();
        if (key && !Object.prototype.hasOwnProperty.call(state.statesByCountry, key)) {
          state.statesByCountry[key] = [];
        }
      });

    builder
      .addCase(fetchRegistrationTrackerHistory.pending, (state, action) => {
        const key = historyTrackerKey(action.meta.arg);
        if (!key) return;
        state.historyLoadingByTrackerId[key] = true;
        delete state.historyErrorByTrackerId[key];
      })
      .addCase(fetchRegistrationTrackerHistory.fulfilled, (state, action) => {
        const { trackerId, items } = action.payload;
        state.historyLoadingByTrackerId[trackerId] = false;
        state.historyByTrackerId[trackerId] = items;
      })
      .addCase(fetchRegistrationTrackerHistory.rejected, (state, action) => {
        const key = historyTrackerKey(action.meta.arg);
        if (!key) return;
        state.historyLoadingByTrackerId[key] = false;
        state.historyErrorByTrackerId[key] =
          parseServerError(action.payload ?? action.error);
      });

    builder
      .addCase(updateRegistrationTrackerStatus.pending, (state) => {
        state.statusSaving = true;
        state.statusError = null;
      })
      .addCase(updateRegistrationTrackerStatus.fulfilled, (state, action) => {
        state.statusSaving = false;
        const { trackerId, status } = action.payload;
        if (Array.isArray(state.list)) {
          state.list = state.list.map((item) =>
            rowMatchesTrackerId(item, trackerId)
              ? patchRowStatus(item, status)
              : item
          );
        }
        if (rowMatchesTrackerId(state.detail, trackerId)) {
          state.detail = patchRowStatus(state.detail, status);
        }
      })
      .addCase(updateRegistrationTrackerStatus.rejected, (state, action) => {
        state.statusSaving = false;
        state.statusError = parseServerError(action.payload ?? action.error);
      });
  },
});

function patchRowStatus(row, status) {
  const base = row && typeof row === "object" ? row : {};
  return { ...base, status };
}

export const {
  clearRegistrationTrackerDetail,
  clearRegistrationTrackerErrors,
  clearRegistrationTrackerTypesError,
  clearRegistrationTrackerRegistratorsError,
  clearRegistrationTrackerStatesError,
  clearRegistrationTrackerStatesCache,
  clearRegistrationTrackerHistoryError,
  clearRegistrationTrackerCommentError,
  clearRegistrationTrackerStatusError,
  clearRegistrationTrackerCommentDeleteError,
} = registrationTrackerSlice.actions;

export default registrationTrackerSlice.reducer;
